import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Phone, CheckCircle, Sparkles, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import * as LucideIcons from 'lucide-react';
import { services, carpetTypes, whyChooseUs, testimonials, blogPosts, companyInfo } from '../data/mock';

const Home = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="Ana Sayfa"
        description="Bahçeşehir, Beylikdüzü, Esenyurt, Başakşehir ve Büyükçekmece'de profesyonel halı ve koltuk yıkama hizmeti. Ücretsiz alım-teslim, 15+ yıl tecrübe."
        keywords="halı yıkama bahçeşehir, koltuk yıkama bahçeşehir, halı yıkama beylikdüzü, halı yıkama esenyurt, halı yıkama başakşehir, halı yıkama büyükçekmece, yorgan yıkama, stor perde temizleme"
        canonical="https://www.bahcesehirduruhaliyikama.com"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-50 via-white to-cyan-50 py-20 overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block">
                <span className="bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold">
                  <Sparkles className="w-4 h-4 inline mr-2" />
                  Profesyonel Temizlik Hizmeti
                </span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Halı ve Koltuk
                <span className="block text-teal-600">Yıkama</span>
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed">
                Modern ekipmanlar ve çevre dostu ürünlerle halı, koltuk ve ev tekstillerinize 
                profesyonel bakım hizmeti sunuyoruz. Ücretsiz alım-teslim ile kapınızdayız!
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href={`tel:${companyInfo.phones[0]}`}>
                  <Button size="lg" className="bg-teal-600 hover:bg-teal-700 text-white text-lg px-8 py-6 rounded-xl">
                    <Phone className="w-5 h-5 mr-2" />
                    Hemen Ara
                  </Button>
                </a>
                <Link to="/hizmetler">
                  <Button size="lg" variant="outline" className="border-2 border-teal-600 text-teal-600 hover:bg-teal-50 text-lg px-8 py-6 rounded-xl">
                    Hizmetleri Keşfet
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
              <div className="flex items-center gap-6 pt-4">
                <div className="flex items-center">
                  <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  <span className="ml-2 text-sm text-gray-600 font-medium">5.0 (200+ Müşteri)</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.pexels.com/photos/35134989/pexels-photo-35134989.jpeg"
                  alt="Profesyonel Halı Yıkama"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/30 to-transparent"></div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">15+</p>
                    <p className="text-sm text-gray-600">Yıllık Tecrübe</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Hizmetlerimiz</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Profesyonel ekipman ve uzman kadromuzla ev tekstillerinize özel bakım hizmetleri sunuyoruz.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => {
              const Icon = LucideIcons[service.icon] || LucideIcons.Sparkles;
              return (
                <Card key={service.id} className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-teal-500 overflow-hidden">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent"></div>
                    <div className="absolute top-4 left-4 w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg">
                      <Icon className="w-6 h-6 text-teal-600" />
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                    <p className="text-gray-600 text-sm mb-4">{service.description}</p>
                    <ul className="space-y-2">
                      {service.features.slice(0, 3).map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-teal-600 mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="text-center mt-10">
            <Link to="/hizmetler">
              <Button size="lg" className="bg-teal-600 hover:bg-teal-700 text-white">
                Tüm Hizmetleri Gör
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Neden Bizi Seçmelisiniz?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Müşteri memnuniyeti odaklı hizmet anlayışımız ve kalite garantimiz ile fark yaratıyoruz.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChooseUs.map((item) => {
              const Icon = LucideIcons[item.icon] || LucideIcons.CheckCircle;
              return (
                <Card key={item.id} className="text-center p-8 hover:shadow-lg transition-shadow border-0 bg-white">
                  <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-teal-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Carpet Types Preview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Yıkanan Halı Türleri</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Her türlü halı için özel bakım ve yıkama hizmetleri sunuyoruz.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {carpetTypes.slice(0, 6).map((carpet) => (
              <Card key={carpet.id} className="overflow-hidden group hover:shadow-xl transition-all border-2 hover:border-teal-500">
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={carpet.image}
                    alt={carpet.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-2xl font-bold text-white mb-1">{carpet.name}</h3>
                    <p className="text-gray-200 text-sm">{carpet.care}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/hali-turleri">
              <Button size="lg" variant="outline" className="border-2 border-teal-600 text-teal-600 hover:bg-teal-50">
                Tüm Halı Türlerini Gör
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-br from-teal-50 to-cyan-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Müşterilerimiz Ne Diyor?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Binlerce mutlu müşterimizin deneyimlerini okuyun.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="p-6 bg-white border-0 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">"{testimonial.comment}"</p>
                <div>
                  <p className="font-semibold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-600">{testimonial.location}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Hizmet Verdiğimiz Bölgeler</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              İstanbul'un birçok bölgesinde ücretsiz alım-teslim hizmeti sunuyoruz.
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
              {companyInfo.servicedAreas.map((area, index) => {
                const areaSlug = area.toLowerCase().replace(/\s+/g, '-').replace('(', '').replace(')', '');
                return (
                  <Link key={index} to={`/blog?bolge=${areaSlug}`}>
                    <Card className="p-6 text-center border-2 border-teal-100 hover:border-teal-500 hover:shadow-lg transition-all cursor-pointer">
                      <MapPin className="w-8 h-8 text-teal-600 mx-auto mb-3" />
                      <p className="font-semibold text-gray-900">{area}</p>
                    </Card>
                  </Link>
                );
              })}
            </div>
            <div className="text-center mt-8">
              <p className="text-gray-600 bg-teal-50 inline-block px-6 py-3 rounded-lg">
                <CheckCircle className="w-5 h-5 inline text-teal-600 mr-2" />
                Tüm bölgelerde <span className="font-bold text-teal-600">ücretsiz alım-teslim</span> hizmeti
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Preview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Blog</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Halı bakımı ve temizlik konusunda faydalı ipuçları ve bilgiler.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {blogPosts.slice(0, 3).map((post) => (
              <Link key={post.id} to={`/blog/${post.slug}`}>
                <Card className="overflow-hidden group hover:shadow-xl transition-all h-full border-2 hover:border-teal-500">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <CardContent className="p-6">
                    <span className="text-xs font-semibold text-teal-600 bg-teal-50 px-3 py-1 rounded-full">
                      {post.category}
                    </span>
                    <h3 className="text-lg font-bold text-gray-900 mt-3 mb-2 line-clamp-2 group-hover:text-teal-600 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 text-sm line-clamp-2">{post.excerpt}</p>
                    <p className="text-xs text-gray-500 mt-3">{new Date(post.date).toLocaleDateString('tr-TR')}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/blog">
              <Button size="lg" variant="outline" className="border-2 border-teal-600 text-teal-600 hover:bg-teal-50">
                Tüm Blog Yazılarını Gör
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-cyan-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Halılarınız Yeni Gibi Olsun!</h2>
          <p className="text-xl mb-8 text-teal-50 max-w-2xl mx-auto">
            Hemen arayın, ücretsiz alım-teslim hizmetimizden yararlanın. 
            2-3 gün içinde tertemiz halılarınıza kavuşun!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${companyInfo.phones[0]}`}>
              <Button size="lg" className="bg-white text-teal-600 hover:bg-gray-100 text-lg px-8 py-6 rounded-xl">
                <Phone className="w-5 h-5 mr-2" />
                {companyInfo.phones[0]}
              </Button>
            </a>
            <Link to="/iletisim">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-teal-600 text-lg px-8 py-6 rounded-xl">
                Online Teklif Al
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
